import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Select, Button, Space, Divider, Spin, DatePicker, Upload } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'underscore';
import dayjs from 'dayjs';
import { InboxOutlined } from '@ant-design/icons';

import { AppDispatch, CombinedStates } from '../../../core/types';
import { getIndicators, generateIndicatorsImportTemplate, importIndicatorsValues, getSchoolIndicatorCategories } from '../../../core/indicator/indicatorApi';
import SchoolSelection from '../SchoolSelection';
import '../SchoolCompare/style.scss';

const { Title, Text } = Typography;
const { Option } = Select;

const SchoolIndicatorsImport: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<number | null>(null);
    const [indicatorFilterCategoryId, setIndicatorFilterCategoryId] = useState<number | 'all'>('all');
    const [selectedIndicators, setSelectedIndicators] = useState<number[]>([]);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [fileList, setFileList] = useState<any[]>([]);

    const {
        schoolIndicatorCategories,
    } = useSelector((state: CombinedStates) => state.indicator);

    useEffect(() => {
        if (isEmpty(schoolIndicatorCategories)) {
            dispatch(getSchoolIndicatorCategories({}));
        }
    }, [dispatch, schoolIndicatorCategories]);

    const handleStartImport = async () => {
        const file = fileList?.[0]?.originFileObj;
        if (!file) {
            console.log('No file selected');
            return;
        }

        try {
            await dispatch(importIndicatorsValues({
                file,
                date: selectedDate.format('YYYY-MM-DD'),
            })).unwrap();

            // Clear the file list after successful import
            setFileList([]);
        } catch (error) {
            console.error('Import failed:', error);
        }
    };

    const {
        indicators,
        isFetching: isIndicatorsFetching,
        isFetchingImportTemplate,
        isImportingValues,
    } = useSelector((state: CombinedStates) => state.indicator);

    // Load indicators if not available
    useEffect(() => {
        if (isEmpty(indicators)) {
            dispatch(getIndicators({}));
        }
    }, [dispatch, indicators]);

    const handleCategorySelect = (categoryId: number | null) => {
        setSelectedCategories(categoryId);
        // Clear individual indicators when categories are selected
        setSelectedIndicators([]);
    };

    const handleIndicatorFilterCategoryChange = (value: number | 'all' | undefined) => {
        setIndicatorFilterCategoryId(value === undefined || value === null ? 'all' : value);
    };

    const handleIndicatorSelect = (indicatorIds: number[]) => {
        setSelectedIndicators(indicatorIds);
        // Clear categories when individual indicators are selected
        setSelectedCategories(null);
    };

    const createImportPayload = () => {
        if (selectedSchools.length === 0) {
            return null;
        }

        const payload: any = {
            school_ids: selectedSchools.map(id => parseInt(id, 10)),
        };

        // Add either category_id or indicator_ids
        if (selectedCategories !== null) {
            payload.category_id = selectedCategories;
        } else if (selectedIndicators.length > 0) {
            payload.indicator_ids = selectedIndicators;
        }

        return payload;
    };

    const handleGenerateImportFile = async () => {
        const payload = createImportPayload();
        if (!payload) {
            console.log('No payload available for import');
            return;
        }

        try {
            const response = await dispatch(generateIndicatorsImportTemplate(payload)).unwrap();

            // Create blob and download Excel file
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `mokyklu_duomenu_importo_sablonas_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Import template generation failed:', error);
        }
    };

    const canGenerateImport = selectedSchools.length > 0
        && (selectedCategories !== null || selectedIndicators.length > 0);

    // Filter indicators based on assigned_scope (all, types, or schools) and optional category filter
    type CategoryWithPivot = { id?: number; pivot?: { category_id?: number } };
    const filteredIndicators = indicators
        .filter(indicator => ['all', 'types', 'schools'].includes(indicator.assigned_scope))
        .filter(indicator => {
            if (indicatorFilterCategoryId === 'all') return true;
            const indicatorCategoryIds = (indicator.categories as CategoryWithPivot[] | undefined)?.map(
                (cat: CategoryWithPivot) => cat?.pivot?.category_id ?? cat?.id,
            ) ?? [];
            return indicatorCategoryIds.includes(indicatorFilterCategoryId);
        });

    return (
        <div className="school-compare">

            <SchoolSelection
                selectedSchools={selectedSchools}
                onChange={setSelectedSchools}
                placeholder="Pasirinkite mokyklas importavimui" />

            {/* Indicators Selection */}
            <Card title="Rodiklių pasirinkimas" className="selection-card">
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <Text strong>Rodiklių kategorijos:</Text>
                        <Select
                            placeholder="Pasirinkite rodiklių kategoriją"
                            value={selectedCategories}
                            onChange={handleCategorySelect}
                            style={{ width: '100%', marginTop: 8 }}
                            allowClear>
                            {schoolIndicatorCategories.map(category => (
                                <Option key={category.id} value={category.id}>
                                    {category.title}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <Divider>arba</Divider>

                    <div>
                        <Text strong>Individualūs rodikliai:</Text>
                        <Row gutter={12} align="middle" style={{ marginTop: 8 }}>
                            <Col xs={24} sm={10} md={8}>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Filtruoti pagal kategoriją:</Text>
                                <Select
                                    value={indicatorFilterCategoryId}
                                    onChange={handleIndicatorFilterCategoryChange}
                                    style={{ width: '100%' }}
                                    allowClear
                                    placeholder="Visi rodikliai">
                                    <Option value="all">Visi rodikliai</Option>
                                    {schoolIndicatorCategories.map(category => (
                                        <Option key={category.id} value={category.id}>
                                            {category.title}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={24} sm={14} md={16}>
                                {isIndicatorsFetching ? (
                                    <div className="loading-container">
                                        <Spin size="small" />
                                        <Text>Kraunami rodikliai...</Text>
                                    </div>
                                ) : (
                                    <>
                                        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Pasirinkite individualius rodiklius:</Text>
                                        <Select
                                            mode="multiple"
                                            placeholder="Pasirinkite individualius rodiklius"
                                            value={selectedIndicators}
                                            onChange={handleIndicatorSelect}
                                            style={{ width: '100%' }}
                                            allowClear
                                            showSearch
                                            filterOption={(input, option) => {
                                                const optionText = String(option?.children || '').toLowerCase();
                                                const inputLower = input.toLowerCase();

                                                // Check if input starts with 'r' or 'R' followed by numbers (ID search)
                                                const idMatch = inputLower.match(/^r?(\d+)$/);
                                                if (idMatch && idMatch[1]) {
                                                    const searchId = idMatch[1];
                                                    return optionText.includes(`r${searchId}`) || optionText.includes(searchId);
                                                }

                                                // Regular text search
                                                return optionText.includes(inputLower);
                                            }}>
                                            {filteredIndicators.map(indicator => (
                                                <Option key={indicator.id} value={indicator.id}>
                                                    R{indicator.id} - {indicator.title}
                                                </Option>
                                            ))}
                                        </Select>
                                    </>
                                )}
                            </Col>
                        </Row>
                    </div>
                </Space>
            </Card>

            {/* Generate Import File Button */}
            <div className="compare-actions">
                <Space>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleGenerateImportFile}
                        disabled={!canGenerateImport}
                        loading={isFetchingImportTemplate}>
                        Generuoti importo failą
                    </Button>
                </Space>
            </div>

            <Divider />

            {/* Import Execution */}
            <Card title="Importo vykdymas" className="selection-card">
                <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                        <Text strong>Pasirinkite, kuriam laikotarpiui norite importuoti rodiklių reikšmes:</Text>
                        <div style={{ marginTop: 8 }}>
                            <DatePicker
                                value={selectedDate}
                                onChange={(date) => setSelectedDate(date || dayjs())}
                                format="YYYY-MM-DD"
                                placeholder="Pasirinkite datą"
                                style={{ width: 200 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                        </div>
                    </div>

                    <Divider />

                    <div>
                        <Text strong>Įkelkite užpildytą Excel (.xlsx) importo failą:</Text>
                        <div style={{ marginTop: 8 }}>
                            <Upload.Dragger
                                name="file"
                                multiple={false}
                                fileList={fileList}
                                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                beforeUpload={() => false}
                                onChange={({ fileList: newFileList }) => setFileList(newFileList.slice(-1))}
                                onRemove={() => { setFileList([]); return true; }}>
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Vilkite ir numeskite failą čia arba spauskite, kad pasirinktumėte</p>
                                <p className="ant-upload-hint">Priimami tik Excel failai (.xlsx, .xls). Failas bus išsiųstas į serverį vėliau.</p>
                            </Upload.Dragger>
                        </div>
                    </div>
                </Space>
            </Card>

            {/* Start Import Button */}
            <div className="compare-actions" style={{ marginTop: 16 }}>
                <Space>
                    <Button
                        type="primary"
                        size="large"
                        onClick={handleStartImport}
                        disabled={fileList.length === 0}
                        loading={isImportingValues}>
                        Pradėti importą
                    </Button>
                </Space>
            </div>
        </div>
    );
};

export default SchoolIndicatorsImport;
